import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Droplets,
  Zap,
  Leaf,
  Calendar,
  Building2,
  Filter,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Eye,
} from "lucide-react";
import Topbar from "Components/Topbar/Topbar";
import { MultiSearchableCombobox } from "Components/MultiSearchableCombobox";
import { useGetBUs } from "../../hooks/reference/useGetBUs";
import { useGetActivites } from "../../hooks/reference/useGetActivites";
import { useGetFilieres } from "../../hooks/reference/useGetFilieres";
import { useGetRegions } from "../../hooks/reference/useGetRegions";
import { useGetSocietes } from "../../hooks/reference/useGetSocietes";
import { useGetSites } from "../../hooks/site/useGetSites";
import type { User as BackendUser } from "../../types/user";
import type { ReportModule, ReportConfig } from "./ReportsPage";
import "./ReportsPage.css";

interface ReportConfigPageProps {
  user: BackendUser;
  module: ReportModule;
  onBack: () => void;
  onPreview: (config: ReportConfig) => void;
}

const MODULE_META: Record<
  ReportModule,
  { title: string; icon: React.ReactNode; accentColor: string; iconBg: string }
> = {
  eau: {
    title: "Rapport Eau",
    icon: <Droplets size={22} />,
    accentColor: "#3b82f6",
    iconBg: "#3b82f6",
  },
  energie: {
    title: "Rapport Énergie",
    icon: <Zap size={22} />,
    accentColor: "#f59e0b",
    iconBg: "#f59e0b",
  },
  carbone: {
    title: "Bilan Carbone",
    icon: <Leaf size={22} />,
    accentColor: "#059669",
    iconBg: "#059669",
  },
};

const MONTHS = [
  { value: 1, label: "Janvier" },
  { value: 2, label: "Février" },
  { value: 3, label: "Mars" },
  { value: 4, label: "Avril" },
  { value: 5, label: "Mai" },
  { value: 6, label: "Juin" },
  { value: 7, label: "Juillet" },
  { value: 8, label: "Août" },
  { value: 9, label: "Septembre" },
  { value: 10, label: "Octobre" },
  { value: 11, label: "Novembre" },
  { value: 12, label: "Décembre" },
];

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;
// Campaign start year: if we're in Oct or later, current campaign started this year
const CURRENT_CAMPAIGN_START = CURRENT_MONTH >= 10 ? CURRENT_YEAR : CURRENT_YEAR - 1;

// List of campaigns, e.g. "24/25", "23/24", ...
const CAMPAIGNS = Array.from({ length: 8 }, (_, i) => {
  const sy = CURRENT_CAMPAIGN_START - i;
  return {
    label: `${String(sy).slice(-2)}/${String(sy + 1).slice(-2)}`,
    startYear: sy,
  };
});

interface FiltersState {
  bus: (number | string)[];
  activites: (number | string)[];
  filieres: (number | string)[];
  regions: (number | string)[];
  societes: (number | string)[];
  sites: (number | string)[];
}

const EMPTY_FILTERS: FiltersState = {
  bus: [],
  activites: [],
  filieres: [],
  regions: [],
  societes: [],
  sites: [],
};

const ReportConfigPage: React.FC<ReportConfigPageProps> = ({ user, module, onBack, onPreview }) => {
  const navigate = useNavigate();
  const meta = MODULE_META[module];

  // Two campaign selectors — N and N-1 (index into CAMPAIGNS array)
  const [campaignNIdx, setCampaignNIdx] = useState(0);
  const [campaignN1Idx, setCampaignN1Idx] = useState(1);

  const campaignN = CAMPAIGNS[campaignNIdx];
  const campaignN1 = CAMPAIGNS[campaignN1Idx];
  // Derived year values passed to report generation (unchanged logic)
  const yearN = campaignN.startYear;
  const yearN1 = campaignN1.startYear;

  // Campaign starts in October — default period to Oct–Dec
  const [startMonth, setStartMonth] = useState(10);
  const [endMonth, setEndMonth] = useState(12);

  const [filters, setFilters] = useState<FiltersState>(EMPTY_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(true);

  // Reference data
  const { data: bus = [] } = useGetBUs();
  const { data: activites = [] } = useGetActivites();
  const { data: filieres = [] } = useGetFilieres();
  const { data: regions = [] } = useGetRegions();
  const { data: societes = [] } = useGetSocietes();
  const { data: sites = [] } = useGetSites();

  const busItems = bus.map((b: any) => ({ value: b.id, label: b.name ?? b.nom ?? String(b.id) }));
  const activiteItems = activites.map((a: any) => ({ value: a.id, label: a.name ?? a.nom ?? String(a.id) }));
  const filiereItems = filieres.map((f: any) => ({ value: f.id, label: f.name ?? f.nom ?? String(f.id) }));
  const regionItems = regions.map((r: any) => ({ value: r.id, label: r.name ?? r.nom ?? String(r.id) }));
  const societeItems = societes.map((s: any) => ({ value: s.id, label: s.name ?? s.nom ?? String(s.id) }));
  const siteItems = sites.map((s: any) => ({ value: s.id, label: s.name ?? s.nom ?? String(s.id) }));

  const activeFilterCount = Object.values(filters).reduce((acc, arr) => acc + arr.length, 0);
  const handleResetFilters = () => setFilters(EMPTY_FILTERS);

  const isCarboneModule = module === "carbone";

  const handleGenerate = () => {
    if (isCarboneModule) return;
    onPreview({ yearN, yearN1, startMonth, endMonth, filters });
  };

  const handleCampaignN = (idx: number) => {
    setCampaignNIdx(idx);
    // Auto-advance N-1 to the next campaign down
    setCampaignN1Idx(Math.min(idx + 1, CAMPAIGNS.length - 1));
  };

  const handleStartMonth = (m: number) => {
    setStartMonth(m);
    if (m > endMonth) setEndMonth(m);
  };

  const monthLabel = (v: number) => MONTHS.find((m) => m.value === v)?.label ?? "";

  return (
    <div className="reports-page">
      <Topbar
        title={meta.title}
        userName={user.username}
        onLogout={() => navigate("/")}
        user={user}
      />

      <div className="reports-content">
        {/* Back + Header */}
        <div className="reports-config-header">
          <button className="reports-back-btn" onClick={onBack} aria-label="Retour">
            <ArrowLeft size={18} />
            <span>Retour</span>
          </button>

          <div className="reports-header-left">
            <div className="reports-icon-wrapper" style={{ background: meta.iconBg }}>
              {meta.icon}
            </div>
            <div>
              <h1 className="reports-title">{meta.title}</h1>
              <p className="reports-subtitle">
                Configurez les périodes et filtres pour générer votre rapport comparatif
              </p>
            </div>
          </div>
        </div>

        <div className="reports-config-layout">
          {/* Left column */}
          <div className="reports-config-main">

            {/* Campaigns N and N-1 */}
            <div className="reports-card">
              <div className="reports-card-header">
                <div className="reports-card-icon" style={{ background: meta.accentColor }}>
                  <Calendar size={16} color="white" />
                </div>
                <div>
                  <h3 className="reports-card-title">Campagnes de comparaison</h3>
                  <p className="reports-card-subtitle">Sélectionnez les deux campagnes à comparer (Oct – Sep)</p>
                </div>
              </div>
              <div className="reports-period-grid">
                <div className="reports-field">
                  <label className="reports-label">Campagne N</label>
                  <select
                    className="reports-select"
                    value={campaignNIdx}
                    onChange={(e) => handleCampaignN(Number(e.target.value))}
                  >
                    {CAMPAIGNS.map((c, i) => (
                      <option key={c.label} value={i}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div className="reports-field">
                  <label className="reports-label">Campagne N-1</label>
                  <select
                    className="reports-select"
                    value={campaignN1Idx}
                    onChange={(e) => setCampaignN1Idx(Number(e.target.value))}
                  >
                    {CAMPAIGNS.map((c, i) => (
                      <option key={c.label} value={i}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Shared period */}
            <div className="reports-card">
              <div className="reports-card-header">
                <div className="reports-card-icon" style={{ background: "#6366f1" }}>
                  <Calendar size={16} color="white" />
                </div>
                <div>
                  <h3 className="reports-card-title">Période analysée</h3>
                  <p className="reports-card-subtitle">Mois de début et de fin appliqués aux deux années</p>
                </div>
              </div>
              <div className="reports-period-grid">
                <div className="reports-field">
                  <label className="reports-label">Mois de début</label>
                  <select
                    className="reports-select"
                    value={startMonth}
                    onChange={(e) => handleStartMonth(Number(e.target.value))}
                  >
                    {MONTHS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div className="reports-field">
                  <label className="reports-label">Mois de fin</label>
                  <select
                    className="reports-select"
                    value={endMonth}
                    onChange={(e) => setEndMonth(Number(e.target.value))}
                  >
                    {MONTHS.filter((m) => m.value >= startMonth).map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="reports-period-badge" style={{ background: "#ede9fe", color: "#6366f1" }}>
                {monthLabel(startMonth)} – {monthLabel(endMonth)}
              </div>
            </div>

            {/* Filters */}
            <div className="reports-card">
              <button
                className="reports-filters-toggle"
                onClick={() => setFiltersOpen((v) => !v)}
              >
                <div className="reports-filters-toggle-left">
                  <div className="reports-card-icon" style={{ background: "#6366f1" }}>
                    <Filter size={16} color="white" />
                  </div>
                  <div>
                    <span className="reports-card-title">Filtres organisationnels</span>
                    {activeFilterCount > 0 && (
                      <span className="reports-filter-badge">{activeFilterCount} actif{activeFilterCount > 1 ? "s" : ""}</span>
                    )}
                  </div>
                </div>
                <div className="reports-filters-toggle-right">
                  {activeFilterCount > 0 && (
                    <button
                      className="reports-reset-btn"
                      onClick={(e) => { e.stopPropagation(); handleResetFilters(); }}
                    >
                      <RotateCcw size={13} />
                      Réinitialiser
                    </button>
                  )}
                  {filtersOpen ? <ChevronUp size={18} color="#94a3b8" /> : <ChevronDown size={18} color="#94a3b8" />}
                </div>
              </button>

              {filtersOpen && (
                <div className="reports-filters-body">
                  <div className="reports-filters-grid">
                    <div className="reports-field">
                      <label className="reports-label">
                        <Building2 size={13} />
                        Business Unit
                      </label>
                      <MultiSearchableCombobox
                        items={busItems}
                        values={filters.bus}
                        placeholder="Toutes les BU"
                        onChange={(v) => setFilters((f) => ({ ...f, bus: v }))}
                        accentColor="#6366f1"
                      />
                    </div>

                    <div className="reports-field">
                      <label className="reports-label">
                        <Filter size={13} />
                        Activité
                      </label>
                      <MultiSearchableCombobox
                        items={activiteItems}
                        values={filters.activites}
                        placeholder="Toutes les activités"
                        onChange={(v) => setFilters((f) => ({ ...f, activites: v }))}
                        accentColor="#6366f1"
                      />
                    </div>

                    <div className="reports-field">
                      <label className="reports-label">
                        <Leaf size={13} />
                        Filière
                      </label>
                      <MultiSearchableCombobox
                        items={filiereItems}
                        values={filters.filieres}
                        placeholder="Toutes les filières"
                        onChange={(v) => setFilters((f) => ({ ...f, filieres: v }))}
                        accentColor="#6366f1"
                      />
                    </div>

                    <div className="reports-field">
                      <label className="reports-label">
                        <Filter size={13} />
                        Région
                      </label>
                      <MultiSearchableCombobox
                        items={regionItems}
                        values={filters.regions}
                        placeholder="Toutes les régions"
                        onChange={(v) => setFilters((f) => ({ ...f, regions: v }))}
                        accentColor="#6366f1"
                      />
                    </div>

                    <div className="reports-field">
                      <label className="reports-label">
                        <Building2 size={13} />
                        Société
                      </label>
                      <MultiSearchableCombobox
                        items={societeItems}
                        values={filters.societes}
                        placeholder="Toutes les sociétés"
                        onChange={(v) => setFilters((f) => ({ ...f, societes: v }))}
                        accentColor="#6366f1"
                      />
                    </div>

                    <div className="reports-field">
                      <label className="reports-label">
                        <Filter size={13} />
                        Site
                      </label>
                      <MultiSearchableCombobox
                        items={siteItems}
                        values={filters.sites}
                        placeholder="Tous les sites"
                        onChange={(v) => setFilters((f) => ({ ...f, sites: v }))}
                        accentColor="#6366f1"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right column: Summary + CTA */}
          <div className="reports-config-sidebar">
            <div className="reports-card reports-summary-card">
              <div className="reports-card-header">
                <div className="reports-card-icon" style={{ background: meta.iconBg }}>
                  {meta.icon}
                </div>
                <div>
                  <h3 className="reports-card-title">Récapitulatif</h3>
                  <p className="reports-card-subtitle">Paramètres du rapport</p>
                </div>
              </div>

              <div className="reports-summary-section">
                <div className="reports-summary-row">
                  <span className="reports-summary-label">Module</span>
                  <span className="reports-summary-value" style={{ color: meta.accentColor, fontWeight: 600 }}>
                    {meta.title}
                  </span>
                </div>

                <div className="reports-summary-divider" />

                <div className="reports-summary-period">
                  <div className="reports-summary-period-item">
                    <span className="reports-summary-period-tag" style={{ background: `${meta.accentColor}18`, color: meta.accentColor }}>N</span>
                    <span className="reports-summary-period-label">
                      Campagne {campaignN.label} ({monthLabel(startMonth).slice(0, 3)} – {monthLabel(endMonth).slice(0, 3)})
                    </span>
                  </div>
                  <div className="reports-summary-period-item">
                    <span className="reports-summary-period-tag" style={{ background: "#f1f5f9", color: "#64748b" }}>N-1</span>
                    <span className="reports-summary-period-label">
                      Campagne {campaignN1.label} ({monthLabel(startMonth).slice(0, 3)} – {monthLabel(endMonth).slice(0, 3)})
                    </span>
                  </div>
                </div>

                <div className="reports-summary-divider" />

                <div className="reports-summary-row">
                  <span className="reports-summary-label">Filtres actifs</span>
                  <span className="reports-summary-value">
                    {activeFilterCount > 0 ? (
                      <span style={{ color: "#6366f1", fontWeight: 600 }}>{activeFilterCount} filtre{activeFilterCount > 1 ? "s" : ""}</span>
                    ) : (
                      <span style={{ color: "#94a3b8" }}>Aucun</span>
                    )}
                  </span>
                </div>
              </div>

              {isCarboneModule ? (
                <div className="reports-preview-banner" style={{ background: "#f8fafc" }}>
                  <p style={{ color: "#94a3b8", fontSize: 13, textAlign: "center", margin: 0 }}>
                    La génération du rapport Carbone n'est pas encore disponible.
                  </p>
                </div>
              ) : (
                <button
                  className="reports-generate-btn"
                  style={{ "--btn-color": meta.accentColor } as React.CSSProperties}
                  onClick={handleGenerate}
                >
                  <Eye size={18} />
                  Prévisualiser le rapport
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportConfigPage;
